import { createSlice } from "@reduxjs/toolkit";
import { asyncStatus } from "../../utils/asyncStatus";
import {
  error_toast_message,
  success_toast_message,
} from "../../utils/toast_message";
import {
  getUserTickets,
  createTicket,
  getTicketById,
  replyToTicket,
  getAdminTickets,
  getAdminTicketById,
  adminReplyToTicket,
  updateTicketStatus,
} from "../../services/supportTicketService";

const initialState = {
  // User tickets
  user_tickets_status: asyncStatus.IDLE,
  user_tickets: [],
  user_tickets_error: null,

  // Create ticket
  create_ticket_status: asyncStatus.IDLE,
  create_ticket_error: null,

  // Single ticket
  current_ticket_status: asyncStatus.IDLE,
  current_ticket: null,
  current_ticket_error: null,

  // Reply to ticket
  reply_ticket_status: asyncStatus.IDLE,
  reply_ticket_error: null,

  // Admin tickets
  admin_tickets_status: asyncStatus.IDLE,
  admin_tickets: [],
  admin_tickets_stats: null,
  admin_tickets_error: null,

  // Admin single ticket
  admin_current_ticket_status: asyncStatus.IDLE,
  admin_current_ticket: null,
  admin_current_ticket_error: null,

  // Admin reply
  admin_reply_status: asyncStatus.IDLE,
  admin_reply_error: null,

  // Update status
  update_status_status: asyncStatus.IDLE,
  update_status_error: null,
};

const supportTicketSlice = createSlice({
  name: "supportTicketSlice",
  initialState,
  reducers: {
    resetCreateTicketStatus: (state) => {
      state.create_ticket_status = asyncStatus.IDLE;
      state.create_ticket_error = null;
    },
    resetReplyStatus: (state) => {
      state.reply_ticket_status = asyncStatus.IDLE;
      state.reply_ticket_error = null;
    },
    resetAdminReplyStatus: (state) => {
      state.admin_reply_status = asyncStatus.IDLE;
      state.admin_reply_error = null;
    },
    clearCurrentTicket: (state) => {
      state.current_ticket = null;
      state.current_ticket_status = asyncStatus.IDLE;
    },
    clearAdminCurrentTicket: (state) => {
      state.admin_current_ticket = null;
      state.admin_current_ticket_status = asyncStatus.IDLE;
    },
  },
  extraReducers: (builder) => {
    // Get User Tickets
    builder.addCase(getUserTickets.pending, (state) => {
      state.user_tickets_status = asyncStatus.LOADING;
    });
    builder.addCase(getUserTickets.fulfilled, (state, { payload }) => {
      state.user_tickets_status = asyncStatus.SUCCEEDED;
      state.user_tickets = payload.tickets || [];
    });
    builder.addCase(getUserTickets.rejected, (state, action) => {
      state.user_tickets_status = asyncStatus.ERROR;
      state.user_tickets_error = action.payload;
      error_toast_message(action.payload);
    });

    // Create Ticket
    builder.addCase(createTicket.pending, (state) => {
      state.create_ticket_status = asyncStatus.LOADING;
    });
    builder.addCase(createTicket.fulfilled, (state, { payload }) => {
      state.create_ticket_status = asyncStatus.SUCCEEDED;
      state.user_tickets = [payload.ticket, ...state.user_tickets];
      success_toast_message(payload.message || "Ticket created successfully");
    });
    builder.addCase(createTicket.rejected, (state, action) => {
      state.create_ticket_status = asyncStatus.ERROR;
      state.create_ticket_error = action.payload;
      error_toast_message(action.payload);
    });

    // Get Ticket By ID
    builder.addCase(getTicketById.pending, (state) => {
      state.current_ticket_status = asyncStatus.LOADING;
    });
    builder.addCase(getTicketById.fulfilled, (state, { payload }) => {
      state.current_ticket_status = asyncStatus.SUCCEEDED;
      state.current_ticket = payload.ticket;
    });
    builder.addCase(getTicketById.rejected, (state, action) => {
      state.current_ticket_status = asyncStatus.ERROR;
      state.current_ticket_error = action.payload;
      error_toast_message(action.payload);
    });

    // Reply to Ticket (User)
    builder.addCase(replyToTicket.pending, (state) => {
      state.reply_ticket_status = asyncStatus.LOADING;
    });
    builder.addCase(replyToTicket.fulfilled, (state, { payload }) => {
      state.reply_ticket_status = asyncStatus.SUCCEEDED;
      if (state.current_ticket) {
        state.current_ticket.replies = [
          ...(state.current_ticket.replies || []),
          payload.reply,
        ];
      }
      success_toast_message("Reply sent successfully");
    });
    builder.addCase(replyToTicket.rejected, (state, action) => {
      state.reply_ticket_status = asyncStatus.ERROR;
      state.reply_ticket_error = action.payload;
      error_toast_message(action.payload);
    });

    // Admin: Get All Tickets
    builder.addCase(getAdminTickets.pending, (state) => {
      state.admin_tickets_status = asyncStatus.LOADING;
    });
    builder.addCase(getAdminTickets.fulfilled, (state, { payload }) => {
      state.admin_tickets_status = asyncStatus.SUCCEEDED;
      state.admin_tickets = payload.tickets || [];
      state.admin_tickets_stats = payload.stats || null;
    });
    builder.addCase(getAdminTickets.rejected, (state, action) => {
      state.admin_tickets_status = asyncStatus.ERROR;
      state.admin_tickets_error = action.payload;
      error_toast_message(action.payload);
    });

    // Admin: Get Ticket By ID
    builder.addCase(getAdminTicketById.pending, (state) => {
      state.admin_current_ticket_status = asyncStatus.LOADING;
    });
    builder.addCase(getAdminTicketById.fulfilled, (state, { payload }) => {
      state.admin_current_ticket_status = asyncStatus.SUCCEEDED;
      state.admin_current_ticket = payload.ticket;
    });
    builder.addCase(getAdminTicketById.rejected, (state, action) => {
      state.admin_current_ticket_status = asyncStatus.ERROR;
      state.admin_current_ticket_error = action.payload;
      error_toast_message(action.payload);
    });

    // Admin: Reply to Ticket
    builder.addCase(adminReplyToTicket.pending, (state) => {
      state.admin_reply_status = asyncStatus.LOADING;
    });
    builder.addCase(adminReplyToTicket.fulfilled, (state, { payload }) => {
      state.admin_reply_status = asyncStatus.SUCCEEDED;
      if (state.admin_current_ticket) {
        state.admin_current_ticket.replies = [
          ...(state.admin_current_ticket.replies || []),
          payload.reply,
        ];
        state.admin_current_ticket.status = payload.ticket?.status || state.admin_current_ticket.status;
      }
      success_toast_message("Reply sent successfully");
    });
    builder.addCase(adminReplyToTicket.rejected, (state, action) => {
      state.admin_reply_status = asyncStatus.ERROR;
      state.admin_reply_error = action.payload;
      error_toast_message(action.payload);
    });

    // Admin: Update Ticket Status
    builder.addCase(updateTicketStatus.pending, (state) => {
      state.update_status_status = asyncStatus.LOADING;
    });
    builder.addCase(updateTicketStatus.fulfilled, (state, { payload }) => {
      state.update_status_status = asyncStatus.SUCCEEDED;
      if (state.admin_current_ticket) {
        state.admin_current_ticket.status = payload.ticket.status;
        state.admin_current_ticket.resolved_at = payload.ticket.resolved_at;
      }
      // Update in the list as well
      const index = state.admin_tickets.findIndex(
        (t) => t.id === payload.ticket.id
      );
      if (index !== -1) {
        state.admin_tickets[index] = {
          ...state.admin_tickets[index],
          ...payload.ticket,
        };
      }
      success_toast_message(payload.message || "Status updated successfully");
    });
    builder.addCase(updateTicketStatus.rejected, (state, action) => {
      state.update_status_status = asyncStatus.ERROR;
      state.update_status_error = action.payload;
      error_toast_message(action.payload);
    });
  },
});

export const {
  resetCreateTicketStatus,
  resetReplyStatus,
  resetAdminReplyStatus,
  clearCurrentTicket,
  clearAdminCurrentTicket,
} = supportTicketSlice.actions;

export default supportTicketSlice.reducer;
